import React, {
  FunctionComponent,
  Dispatch,
  SetStateAction,
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";
import sepReasons from "./sepReasons.json";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import PlanInfo from "./../PlanInfo";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  dateFormatter,
  getValidationObject,
  EnrollmentPages,
  isValidDate,
  limitInputLengthCustomSelector,
} from "../../../helpers";
import { FocusableHeader, ScrollTable } from "../../../components";
import {
  Choice,
  Close,
  DateField,
  TextField,
  HelpDrawerToggle,
  HelpDrawer,
  Alert,
} from "@cmsgov/ds-medicare-gov";
import { EnrollData, MCTDate } from "../../../@types/index";
import { withRouter, RouteComponentProps } from "react-router";
import routes from "../../../app/routes";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { useFlags } from "launchdarkly-react-client-sdk";
import { linkToSpecialEnrollmentPeriodInfo } from "../../../helpers/CONSTANTS";
import { AppContext } from "../../../app/store";

const SpecialEnrollmentChoice: FunctionComponent<{
  reason: {
    reasonCode: string;
    label: string;
    requiresDate: boolean;
    dateLabel?: string;
    alertLabel?: string;
    ldFlag?: string;
  };
  setEnrollData: Dispatch<SetStateAction<EnrollData>>;
  enrollData: EnrollData;
  formSubmitted: boolean;
}> = ({ reason, setEnrollData, enrollData, formSubmitted }) => {
  const t = useTranslate();
  const flags = useFlags();

  const {
    alertLabel,
    label,
    reasonCode,
    requiresDate,
    dateLabel,
    ldFlag,
  } = reason;
  const displayChoice = ldFlag ? flags[ldFlag] : true;

  const savedReason = enrollData.sepReasons.find(r => r.key === label);

  let dateErrorMessage = "";

  const monthName = `month.${label}`;
  const dayName = `day.${label}`;
  const yearName = `year.${label}`;

  if (
    formSubmitted &&
    savedReason &&
    savedReason.requiresDate &&
    (!savedReason.date || !isValidDate(savedReason.date))
  ) {
    dateErrorMessage = t("sep_form.enter_valid_date");
  }

  return displayChoice ? (
    <Choice
      type="checkbox"
      name={t(label)}
      className="ds-u-margin-bottom--2"
      value={reasonCode}
      checkedChildren={
        <>
          {(alertLabel || requiresDate) && (
            <div className="ds-u-margin-left--5 ds-u-margin-bottom--4">
              {alertLabel && (
                <Alert role="alert" hideIcon={true}>
                  {t(alertLabel)}
                </Alert>
              )}

              {requiresDate && dateLabel && (
                <DateField
                  dateFormatter={dateFormatter}
                  errorMessage={dateErrorMessage}
                  monthName={monthName}
                  dayName={dayName}
                  yearName={yearName}
                  onChange={(
                    _: ChangeEvent<HTMLInputElement>,
                    date: MCTDate
                  ): void => {
                    limitInputLengthCustomSelector(
                      `input[name='${monthName}']`,
                      2
                    );
                    limitInputLengthCustomSelector(
                      `input[name='${dayName}']`,
                      2
                    );
                    limitInputLengthCustomSelector(
                      `input[name='${yearName}']`,
                      4
                    );

                    setEnrollData({
                      ...enrollData,
                      sepReasons: enrollData.sepReasons.map(r => {
                        if (r.key === label) {
                          r.date = date;
                        }

                        return r;
                      }),
                    });
                  }}
                  label={t(dateLabel)}
                  hint={t("dob.hint")}
                  dayLabel={t("dob.day_label")}
                  monthLabel={t("dob.month_label")}
                  yearLabel={t("dob.year_label")}
                />
              )}
            </div>
          )}
        </>
      }
      checked={!!enrollData.sepReasons.find(r => r.key === label)}
      onChange={(): void => {
        const index = enrollData.sepReasons.findIndex(r => r.key === label);

        if (index >= 0) {
          setEnrollData({
            ...enrollData,
            sepReasons: enrollData.sepReasons.filter(r => r.key !== label),
          });
        } else {
          setEnrollData({
            ...enrollData,
            sepReasons: [
              ...enrollData.sepReasons,
              { code: reasonCode, key: label, requiresDate },
            ],
          });
        }
      }}
    >
      {t(label)}
    </Choice>
  ) : (
    <></>
  );
};

const SepEnrollments: FunctionComponent<EnrollmentFormProps &
  RouteComponentProps> = ({
  enrollData,
  setEnrollData,
  nextPage,
  plan,
  inReview,
  history,
}) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppContext);

  const [showLearnMore, setShowLearnMore] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [reasonsValid, setReasonsValid] = useState(false);
  const [otherReasonValid, setOtherReasonValid] = useState(true);

  const alertDiv = useRef<HTMLDivElement>(null);

  const {
    initialEnrollment: ieReasons,
    specialEnrollment: seReasons,
    medicareAdvantagePlan: maReasons,
  } = sepReasons;

  const next = (): void => {
    const {
      isValid,
      sepOtherReasonValidity,
      sepReasonsValidity,
    } = getValidationObject(enrollData, plan, EnrollmentPages.Sep);

    setReasonsValid(!!sepReasonsValidity);
    setOtherReasonValid(!!sepOtherReasonValidity);
    setFormIsValid(isValid);
    setFormSubmitted(true);

    if (isValid) {
      nextPage();
    }
  };

  useEffect(() => {
    if (formSubmitted && !formIsValid) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [formSubmitted]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.start_your_enrollment")}
      />

      <div>
        <PlanInfo plan={plan} />

        <p className="ds-u-margin-top--5">
          {t("sep_form.enrollment_period_qualify")}
        </p>

        <ScrollTable>
          <thead>
            <tr>
              <th>{t("sep_form.enrollment_period")}</th>
              <th>{t("sep_form.coverage_effective_date")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p className="ds-u-margin-y--0 ds-u-font-weight--bold">
                  {t("sep_form.open_enrollment_period")}
                </p>
                <p className="ds-u-margin-y--0">{t("sep_form.oe_dates")}</p>
              </td>
              <td>{t("sep_form.oe_coverage_date")}</td>
            </tr>
            <tr>
              <td>
                <p className="ds-u-margin-y--0 ds-u-font-weight--bold">
                  {t("sep_form.special_enrollment_period")}
                </p>
                <p className="ds-u-margin-top--1">
                  {t("sep_form.sep_explain")}
                </p>
              </td>
              <td>{t("sep_form.sep_coverage_date")}</td>
            </tr>
            <tr>
              <td>
                <p className="ds-u-margin-y--0 ds-u-font-weight--bold">
                  {t("sep_form.initial_enrollment_period")}
                </p>
                <p className="ds-u-margin-y--0">
                  {t("sep_form.new_to_medicare")}
                </p>
                <p className="ds-u-margin-top--1">
                  {t("sep_form.iep_explain")}
                </p>
              </td>
              <td>
                <FormattedHTMLMessage id="sep_form.iep_coverage_date" />
              </td>
            </tr>
            <tr>
              <td>
                <p className="ds-u-margin-y--0 ds-u-font-weight--bold">
                  {t("sep_form.ma_oe_period")}
                </p>
                <p className="ds-u-margin-y--0">{t("sep_form.ma_oe_dates")}</p>
                <p className="ds-u-margin-top--1">
                  <FormattedHTMLMessage id="sep_form.ma_oe_explain" />
                </p>
              </td>
              <td>{t("sep_form.ma_oe_coverage_date")}</td>
            </tr>
          </tbody>
        </ScrollTable>

        <h3 className="ds-h3 ds-u-margin-bottom--0">
          {t("sep_form.confirm_enroll_period")}
        </h3>
        <p className="ds-u-margin-top--1">{t("sep_form.only_enroll_if")}</p>

        <div ref={alertDiv}>
          {!formIsValid && formSubmitted && (
            <Alert
              className="ds-u-margin-top--2"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>

        <p className="ds-u-margin-bottom--0 ds-u-margin-top--7">
          {t("sep_form.click_all_that_apply")}
        </p>
        {!reasonsValid && formSubmitted && (
          <p className="ds-u-margin-bottom--0 ds-u-color--error">
            {t("sep_form.select_at_least_one")}
          </p>
        )}

        <h3 className="ds-h3 ds-u-margin-top--1">
          {t("sep_form.initial_enrollment_period")}
        </h3>
        {ieReasons.map((reason, i) => (
          <SpecialEnrollmentChoice
            key={i}
            reason={reason}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        ))}

        <h3 className="ds-h3">{t("sep_form.ma_oe_period")}</h3>
        {maReasons.map((reason, i) => (
          <SpecialEnrollmentChoice
            key={i}
            reason={reason}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        ))}

        <h3 className="ds-h3">{t("sep_form.special_enrollment_period")}</h3>
        <a
          href={linkToSpecialEnrollmentPeriodInfo(language)}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t("sep_form.learn_more")}
        </a>

        {showLearnMore && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              heading={t("sep_help.qualify_sep")}
              onCloseClick={(): void => setShowLearnMore(false)}
              closeButtonText={<Close />}
              ariaLabel={t("close_help_drawer")}
            >
              <div>
                <h4 className="ds-u-margin-bottom--0">
                  {t("sep_help.no_situation_header")}
                </h4>
                <p className="ds-u-margin-top--1">
                  {t("sep_help.no_situation_body")}
                </p>

                <h4 className="ds-u-margin-top--4">
                  {t("sep_help.other_circumstances")}
                </h4>
                <h4 className="ds-u-margin-bottom--0">
                  {t("sep_help.moving_header")}
                </h4>
                <p className="ds-u-margin-top--1">
                  <FormattedHTMLMessage id="sep_help.moving_body" />
                </p>

                <h4 className="ds-u-margin-bottom--0">
                  {t("sep_help.diff_plan_header")}
                </h4>
                <p className="ds-u-margin-top--1">
                  {t("sep_help.diff_plan_body")}
                </p>

                <h4 className="ds-u-margin-bottom--0">
                  {t("sep_help.weather_header")}
                </h4>
                <p className="ds-u-margin-top--1">
                  <FormattedHTMLMessage id="sep_help.weather_body" />
                </p>

                <h4 className="ds-u-margin-bottom--0">
                  {t("sep_help.last_12_header")}
                </h4>
                <p className="ds-u-margin-top--1">
                  <FormattedHTMLMessage id="sep_help.last_12_body" />
                </p>

                <h4 className="ds-u-margin-bottom--0">
                  {t("sep_help.now_65_header")}
                </h4>
                <p className="ds-u-margin-top--1">
                  {t("sep_help.now_65_body")}
                </p>
              </div>
            </HelpDrawer>
          </div>
        )}

        <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
          {t("sep_form.change_where_you_live")}
        </h4>
        {seReasons.changeLocation.map((reason, i) => (
          <SpecialEnrollmentChoice
            key={i}
            reason={reason}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        ))}

        <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
          {t("sep_form.you_lose_coverage")}
        </h4>
        {sepReasons.specialEnrollment.loseCoverage.map((reason, i) => (
          <SpecialEnrollmentChoice
            key={i}
            reason={reason}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        ))}

        <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
          {t("sep_form.special_situations")}
        </h4>
        {seReasons.otherSpecialSituations.map((reason, i) => (
          <SpecialEnrollmentChoice
            key={i}
            reason={reason}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        ))}
        {plan.high_performing && (
          <SpecialEnrollmentChoice
            reason={{
              requiresDate: false,
              label: "sep_reason.five_star",
              reasonCode: "5ST",
            }}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        )}

        <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
          {t("sep_form.other_circumstances")}
        </h4>
        <HelpDrawerToggle
          helpDrawerOpen={showLearnMore}
          showDrawer={(): void => {
            setShowLearnMore(true);
          }}
        >
          {t("sep_form.learn_more.other_circumstances")}
        </HelpDrawerToggle>
        {seReasons.otherCircumstances.map((reason, i) => (
          <SpecialEnrollmentChoice
            key={i}
            reason={reason}
            setEnrollData={setEnrollData}
            enrollData={enrollData}
            formSubmitted={formSubmitted}
          />
        ))}
        <Choice
          type="checkbox"
          name={t("sep_reason.other")}
          value={"OTH"}
          checked={enrollData.hasSepOtherReason}
          onChange={(): void => {
            const otherLabel = "sep_reason.other";

            const index = enrollData.sepReasons.findIndex(
              r => r.key === otherLabel
            );

            if (index >= 0) {
              setEnrollData({
                ...enrollData,
                sepReasons: enrollData.sepReasons.filter(
                  r => r.key !== otherLabel
                ),
                hasSepOtherReason: false,
                sepOtherReason: "",
              });
            } else {
              setEnrollData({
                ...enrollData,
                sepReasons: [
                  ...enrollData.sepReasons,
                  {
                    code: "OTH",
                    key: otherLabel,
                    requiresDate: false,
                  },
                ],
                hasSepOtherReason: true,
              });
            }
          }}
          checkedChildren={
            <div className="ds-u-margin-left--7">
              <TextField
                errorMessage={!otherReasonValid && t("sep_form.enter_a_reason")}
                name={t("sep_form.other_reason")}
                label={t("sep_form.other_reason")}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    sepOtherReason: e.currentTarget.value,
                  });
                }}
              />
            </div>
          }
        >
          {t("sep_reason.other")}
        </Choice>

        <h4 className="ds-h4 ds-u-margin-top--7">
          {t("sep_form.nothing_matches_situation")}
        </h4>
        <p className="ds-u-margin-top--1">{t("sep_form.contact_plan")}</p>

        <BackNextButtons
          nextPage={next}
          previousPage={(): void => {
            history.push(routes.searchResults);
          }}
          inReview={inReview}
          nextText={t("sep_form.continue_enrollment")}
        />
      </div>
    </div>
  );
};

export default withRouter(SepEnrollments);
